import { useCallback, useEffect, useRef } from 'react';
import { startTransition } from 'react';
import type {
  MRT_PaginationState,
  MRT_SortingState,
  MRT_ColumnSizingState,
  MRT_ColumnPinningState,
} from 'material-react-table';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import {
  sanitizeColumnVisibility,
  getInitialColumnVisibility,
  getDefaultColumnOrder,
} from './columns';
import {
  normalizeSavedViewConfig,
  sanitizeColumnFilters,
  buildViewPayload,
  applyOpenTrackPreset,
  type ReportFiltersState,
  type SavedViewState,
  type SavedViewPayload,
  type ProviderSource,
} from './viewState';
import { saveProviderViewState, loadProviderViewState } from '../../services/providerViewStorage';
import type { SharedView, ViewTemplate } from '../../hooks/useSharedViews';
import { useNotification } from '../../components/NotificationProvider';

export interface TableState {
  date: { startDate: string; endDate: string };
  globalFilter: string;
  sorting: MRT_SortingState;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  columnSizing: MRT_ColumnSizingState;
  columnPinning: MRT_ColumnPinningState;
  density: 'compact' | 'comfortable' | 'spacious';
  compactHeaders: boolean;
  zebraRows: boolean;
  pagination: MRT_PaginationState;
  filterState: ReportFiltersState;
  providerSource: ProviderSource;
}

export interface TableSetters {
  setDate: (v: { startDate: string; endDate: string }) => void;
  setGlobalFilter: (v: string) => void;
  setSorting: React.Dispatch<React.SetStateAction<MRT_SortingState>>;
  setColumnVisibility: (v: Record<string, boolean>) => void;
  setColumnOrder: (v: string[]) => void;
  setColumnSizing: (v: MRT_ColumnSizingState) => void;
  setColumnPinning: (v: MRT_ColumnPinningState) => void;
  setDensity: (v: 'compact' | 'comfortable' | 'spacious') => void;
  setCompactHeaders: (v: boolean) => void;
  setZebraRows: (v: boolean) => void;
  setPagination: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  setFilterState: React.Dispatch<React.SetStateAction<ReportFiltersState>>;
  setProviderSource: (v: ProviderSource) => void;
  setError: (v: string) => void;
}

interface UseReportViewManagerOptions {
  state: TableState;
  setters: TableSetters;
  tenantSlug?: string;
  tenantId?: string;
  savedViews: SavedViewState[];
  setSavedViews: (v: SavedViewState[]) => void;
  selectedViewName: string;
  setSelectedViewName: (v: string) => void;
  sharedViews: SharedView[];
  viewTemplates: ViewTemplate[];
  userPermissions: { canShareViews: boolean; canManageTemplates: boolean; isAdmin: boolean };
  createSharedView: (data: any) => Promise<any>;
  updateSharedView: (id: string, data: any) => Promise<any>;
  deleteSharedView: (id: string) => Promise<any>;
  trackViewUsage: (id: string, type: 'shared' | 'template') => Promise<void>;
  refreshViewTemplates: () => Promise<void>;
  updateViewTemplate: (id: string, data: any) => Promise<any>;
  lastManualFiltersRef: React.MutableRefObject<ReportFiltersState | null>;
}

export interface ReportViewManagerResult {
  applyTableConfig: (config?: Partial<SavedViewState> | null) => void;
  applySavedView: (v: SavedViewState) => void;
  // handleProviderChange: (next: 'opentrack' | 'dealer_vault' | 'both') => void;
  handleProviderChange: (next: ProviderSource) => void;
  getCurrentViewPayload: () => SavedViewPayload;
  handleExistingTemplateUpdate: (
    template: ViewTemplate,
    trimmedName: string,
    description?: string,
  ) => Promise<boolean>;
  handleApplySharedView: (
    view: SharedView | ViewTemplate,
    type: 'shared' | 'template',
  ) => Promise<void>;
  handleSaveSharedView: (name: string, description?: string) => Promise<void>;
}

/**
 * Manages saved views, shared views, templates, and provider switching
 * for the OLSG Daily Report. Extracted from Reports.tsx.
 */
export function useReportViewManager({
  state,
  setters,
  tenantSlug,
  tenantId,
  selectedViewName,
  setSelectedViewName,
  sharedViews,
  viewTemplates,
  createSharedView,
  updateSharedView,
  trackViewUsage,
  refreshViewTemplates,
  updateViewTemplate,
  lastManualFiltersRef,
}: UseReportViewManagerOptions): ReportViewManagerResult {
  const appliedDefaultRef = useRef<Record<string, boolean>>({});
  const { showSuccess, showError } = useNotification();

  const defaultViewQuery = useQuery({
    queryKey: ['report-default-view', tenantId, state.providerSource],
    enabled: Boolean(tenantId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    queryFn: async () => {
      const params = new URLSearchParams({
        reportId: 'afl-daily',
        providerSource: state.providerSource,
      });
      const response = await apiClient.get(
        `/api/prefs/shared-views/default-view?${params.toString()}`,
      );
      if (!response.ok) {
        const message = await response.text().catch(() => '');
        throw new Error(message || 'Failed to load default view');
      }
      return response.json();
    },
  });

  const applyTableConfig = useCallback(
    (config?: Partial<SavedViewState> | null) => {
      if (!config) return;
      const normalized = normalizeSavedViewConfig(config);
      if (!normalized) return;

      startTransition(() => {
        try {
          if (normalized.providerSource && normalized.providerSource !== state.providerSource) {
            setters.setProviderSource(normalized.providerSource);
          }
          if (normalized.date) setters.setDate(normalized.date);
          if (normalized.globalFilter !== undefined)
            setters.setGlobalFilter(normalized.globalFilter);
          if (normalized.sorting) setters.setSorting(normalized.sorting);
          if (normalized.columnVisibility) {
            const sanitized = sanitizeColumnVisibility(normalized.columnVisibility);
            setters.setColumnVisibility(sanitized);
            localStorage.setItem('afl_column_visibility', JSON.stringify(sanitized));
          }
          if (normalized.columnOrder) {
            setters.setColumnOrder(normalized.columnOrder);
            localStorage.setItem('afl_column_order', JSON.stringify(normalized.columnOrder));
          }
          if (normalized.columnSizing) {
            setters.setColumnSizing(normalized.columnSizing);
            localStorage.setItem('afl_column_sizing', JSON.stringify(normalized.columnSizing));
          }
          if (normalized.columnPinning) {
            setters.setColumnPinning(normalized.columnPinning);
            localStorage.setItem('afl_column_pinning', JSON.stringify(normalized.columnPinning));
          }
          if (normalized.density) {
            setters.setDensity(normalized.density);
            localStorage.setItem('afl_density', normalized.density);
          }
          if (normalized.compactHeaders !== undefined) {
            setters.setCompactHeaders(normalized.compactHeaders);
            localStorage.setItem('afl_compact_headers', normalized.compactHeaders ? '1' : '0');
            document.documentElement.toggleAttribute(
              'data-compact-headers',
              normalized.compactHeaders,
            );
          }
          if (normalized.zebraRows !== undefined) {
            setters.setZebraRows(normalized.zebraRows);
            localStorage.setItem('afl_zebra_rows', normalized.zebraRows ? '1' : '0');
          }

          const filterPatch: Partial<ReportFiltersState> = {};
          if (normalized.typeCodeFilter)
            filterPatch.typeCodeFilter = [...normalized.typeCodeFilter];
          if (normalized.lineTypeFilter)
            filterPatch.lineTypeFilter = [...normalized.lineTypeFilter];
          if (normalized.linePayFilter) filterPatch.linePayFilter = [...normalized.linePayFilter];
          if (normalized.coveredLineFilter)
            filterPatch.coveredLineFilter = [...normalized.coveredLineFilter];
          if (normalized.columnFilters)
            filterPatch.columnFilters = sanitizeColumnFilters(normalized.columnFilters);
          if (Object.keys(filterPatch).length) {
            setters.setFilterState((prev) => ({ ...prev, ...filterPatch }));
          }

          if (normalized.pageSize) {
            setters.setPagination(() => ({ pageIndex: 0, pageSize: normalized.pageSize! }));
          }
        } catch (err) {
          console.error('Failed to apply saved view', err);
          setters.setError('Failed to apply saved view. Please refresh or reselect a view.');
        }
      });
    },
    [state.providerSource, setters],
  );

  const applySavedView = useCallback(
    (v: SavedViewState) => applyTableConfig(v),
    [applyTableConfig],
  );

  // const handleProviderChange = useCallback(
  //   (next: 'opentrack' | 'dealer_vault' | 'both') => {
  const handleProviderChange = useCallback(
    (next: ProviderSource) => {
      if (state.providerSource === next) return;

      if (tenantSlug) {
        saveProviderViewState(state.providerSource, tenantSlug, {
          columnVisibility: state.columnVisibility,
          columnOrder: state.columnOrder,
          columnSizing: state.columnSizing,
          columnPinning: state.columnPinning,
          filterState: state.filterState,
          sorting: state.sorting,
          density: state.density,
          compactHeaders: state.compactHeaders,
          zebraRows: state.zebraRows,
          pageSize: state.pagination.pageSize,
        });
      }

      setters.setProviderSource(next);
      setters.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    },
    [state, tenantSlug, setters],
  );

  // Load provider-specific state when provider changes
  const prevProviderRef = useRef(state.providerSource);
  const prevTenantSlugRef = useRef(tenantSlug);
  useEffect(() => {
    if (!tenantSlug) return;
    // Only run when provider or tenant actually changes, not on every render
    if (
      prevProviderRef.current === state.providerSource &&
      prevTenantSlugRef.current === tenantSlug
    ) {
      return;
    }
    prevProviderRef.current = state.providerSource;
    prevTenantSlugRef.current = tenantSlug;
    appliedDefaultRef.current = {};

    const savedState = loadProviderViewState(state.providerSource, tenantSlug);
    if (savedState) {
      applyTableConfig({
        columnVisibility: savedState.columnVisibility,
        columnOrder: savedState.columnOrder,
        columnSizing: savedState.columnSizing,
        columnPinning: savedState.columnPinning,
        sorting: savedState.sorting,
        density: savedState.density,
        compactHeaders: savedState.compactHeaders,
        zebraRows: savedState.zebraRows,
        typeCodeFilter: savedState.filterState.typeCodeFilter,
        lineTypeFilter: savedState.filterState.lineTypeFilter,
        linePayFilter: savedState.filterState.linePayFilter,
        coveredLineFilter: savedState.filterState.coveredLineFilter,
        columnFilters: savedState.filterState.columnFilters,
        pageSize: savedState.pageSize,
        providerSource: savedState.providerSource,
      });
    } else {
      // No saved state for this tenant+provider — reset to defaults
      const defaultVis = sanitizeColumnVisibility(getInitialColumnVisibility(true));
      setters.setColumnVisibility(defaultVis);
      localStorage.setItem('afl_column_visibility', JSON.stringify(defaultVis));

      const defaultOrder = getDefaultColumnOrder();
      setters.setColumnOrder(defaultOrder);
      localStorage.setItem('afl_column_order', JSON.stringify(defaultOrder));

      setters.setColumnSizing({});
      localStorage.setItem('afl_column_sizing', '{}');

      setters.setColumnPinning({ left: ['Date', 'RO Number'], right: [] });
      localStorage.setItem(
        'afl_column_pinning',
        JSON.stringify({ left: ['Date', 'RO Number'], right: [] }),
      );

      setters.setSorting([{ id: 'Date', desc: false }]);
      setters.setGlobalFilter('');
      setters.setPagination((prev) => ({ pageIndex: 0, pageSize: prev.pageSize }));

      setters.setFilterState((prev) => {
        if (state.providerSource === 'opentrack') {
          if (!lastManualFiltersRef.current) lastManualFiltersRef.current = prev;
          return applyOpenTrackPreset(prev);
        }
        if (lastManualFiltersRef.current) {
          const restored = lastManualFiltersRef.current;
          lastManualFiltersRef.current = null;
          return restored;
        }
        return prev;
      });
    }
  }, [state.providerSource, tenantSlug, applyTableConfig, setters, lastManualFiltersRef]);

  // Apply default view based on tenant and provider
  useEffect(() => {
    if (!tenantSlug) return;
    const defaultKey = `${tenantSlug}|${state.providerSource}`;
    const isDefaultSelected = selectedViewName?.startsWith('default:');
    const selectedDefaultProvider = isDefaultSelected
      ? (selectedViewName?.split(':')[1] as ProviderSource)
      : null;
    if (
      selectedViewName &&
      (!isDefaultSelected || selectedDefaultProvider !== state.providerSource)
    )
      return;
    if (appliedDefaultRef.current[defaultKey]) return;

    const rawDefault = defaultViewQuery.data?.defaultView?.viewConfig;
    const serverConfig = normalizeSavedViewConfig(rawDefault);
    if (!serverConfig) return;

    applyTableConfig(serverConfig);
    appliedDefaultRef.current[defaultKey] = true;
    const defaultName = `default:${state.providerSource}`;
    setSelectedViewName(defaultName);
    localStorage.setItem('afl_selected_view', defaultName);
  }, [
    tenantSlug,
    state.providerSource,
    selectedViewName,
    defaultViewQuery.data,
    applyTableConfig,
    setSelectedViewName,
  ]);

  // Apply default template on initial load
  useEffect(() => {
    if (viewTemplates.length > 0 && !selectedViewName) {
      const defaultTemplate = viewTemplates.find((t) => t.isDefault);
      if (defaultTemplate) {
        const normalized = normalizeSavedViewConfig(defaultTemplate.templateConfig);
        if (normalized) applyTableConfig(normalized);
        const displayName = `template:${defaultTemplate.id}`;
        setSelectedViewName(displayName);
        localStorage.setItem('afl_selected_view', displayName);
      }
    }
  }, [viewTemplates, selectedViewName, applyTableConfig, setSelectedViewName]);

  const getCurrentViewPayload = useCallback(
    () =>
      buildViewPayload({
        date: state.date,
        sorting: state.sorting,
        columnVisibility: state.columnVisibility,
        columnOrder: state.columnOrder,
        columnSizing: state.columnSizing,
        columnPinning: state.columnPinning,
        density: state.density,
        compactHeaders: state.compactHeaders,
        zebraRows: state.zebraRows,
        globalFilter: state.globalFilter,
        filters: state.filterState,
        pageSize: state.pagination.pageSize,
        providerSource: state.providerSource,
      }),
    [state],
  );

  const handleExistingTemplateUpdate = useCallback(
    async (template: ViewTemplate, trimmedName: string, description?: string) => {
      const templateConfig = getCurrentViewPayload();
      try {
        const result = await updateViewTemplate(template.id, {
          name: trimmedName,
          description: description?.trim() || template.description,
          templateConfig,
        });
        if (!result.success) throw new Error(result.error || 'Failed to update view template');
        await refreshViewTemplates();
        await trackViewUsage(template.id, 'template');

        const normalized =
          normalizeSavedViewConfig(result.template?.templateConfig) ?? templateConfig;
        applyTableConfig(normalized);
        showSuccess(`Updated "${trimmedName}" template successfully!`);
        return true;
      } catch (error: any) {
        console.error('Failed to update view template:', error);
        showError(`Failed to update template: ${error.message}`);
        throw error;
      }
    },
    [
      applyTableConfig,
      getCurrentViewPayload,
      refreshViewTemplates,
      trackViewUsage,
      updateViewTemplate,
      showSuccess,
      showError,
    ],
  );

  const handleApplySharedView = useCallback(
    async (view: SharedView | ViewTemplate, type: 'shared' | 'template') => {
      try {
        await trackViewUsage(view.id, type);
        const rawConfig =
          type === 'template'
            ? (view as ViewTemplate).templateConfig
            : (view as SharedView).viewConfig;
        const config = normalizeSavedViewConfig(rawConfig);
        if (!config) return;
        applyTableConfig(config);
        const displayName = type === 'template' ? `template:${view.id}` : `shared:${view.id}`;
        setSelectedViewName(displayName);
        localStorage.setItem('afl_selected_view', displayName);
      } catch (error) {
        console.error('Error applying shared view:', error);
      }
    },
    [applyTableConfig, trackViewUsage, setSelectedViewName],
  );

  const handleSaveSharedView = useCallback(
    async (name: string, description?: string) => {
      try {
        const sharedName = name.trim();
        if (!sharedName) throw new Error('A name is required to save a shared view');
        const existing = sharedViews.find((v) => v.name === sharedName);
        const payload = getCurrentViewPayload();
        let result: { success: boolean; error?: string; sharedView?: any };
        if (existing) {
          result = await updateSharedView(existing.id, {
            name: sharedName,
            description,
            viewConfig: payload as any,
          });
        } else {
          result = await createSharedView({
            name: sharedName,
            description,
            reportId: 'afl-daily',
            viewConfig: payload as any,
          });
        }
        if (result.success) {
          const sharedViewId = result.sharedView?.id;
          if (sharedViewId) {
            setSelectedViewName(`shared:${sharedViewId}`);
            localStorage.setItem('afl_selected_view', `shared:${sharedViewId}`);
          }
        } else {
          showError(`Failed to save shared view: ${result.error}`);
        }
      } catch (error) {
        console.error('Error saving shared view:', error);
        showError('Failed to save shared view. Please try again.');
      }
    },
    [
      createSharedView,
      getCurrentViewPayload,
      sharedViews,
      updateSharedView,
      setSelectedViewName,
      showError,
    ],
  );

  return {
    applyTableConfig,
    applySavedView,
    handleProviderChange,
    getCurrentViewPayload,
    handleExistingTemplateUpdate,
    handleApplySharedView,
    handleSaveSharedView,
  };
}
